import { Box, Typography, Modal, ModalDialog, ModalClose, Input, FormControl, FormLabel } from "@mui/joy";
import { Trash2, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../../../shared";
import { useProject as useProjectQuery } from "../../../../../../hooks/query";
import { useNotification } from "../../../../../../hooks";

interface DangerZoneProps {
    projectId?: string;
    projectName?: string;
}

/** Everything removed by `deleteProject`, via explicit cleanup or ON DELETE CASCADE. */
const DELETE_CONSEQUENCES = [
    "Every workflow task and subtask, along with their notes and progress",
    "All roof measurements and inspection attributes captured for this job",
    "All document records, including generated contracts, invoices and inspection reports",
    "The material cart and any supplier selections made for this project",
    "Scheduled milestones and the entire project timeline history",
    "The lead record that links this job back to its source",
];

export function DangerZone({ projectId, projectName }: DangerZoneProps) {
    const navigate = useNavigate();
    const { showError, showSuccess } = useNotification();
    const { useDeleteProject } = useProjectQuery();
    const deleteProjectSvc = useDeleteProject();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [confirmText, setConfirmText] = useState("");

    const isDeleting = deleteProjectSvc.isPending;
    const canConfirm = !projectName || confirmText.trim() === projectName.trim();

    function handleDeleteClick() {
        setConfirmText("");
        setShowDeleteDialog(true);
    }

    async function handleDeleteConfirm() {
        if (!canConfirm || !projectId) return;

        try {
            await deleteProjectSvc.mutateAsync(projectId);
            setShowDeleteDialog(false);
            showSuccess(projectName ? `"${projectName}" was deleted.` : "Project deleted.");
            navigate("/project/list");
        } catch (e: any) {
            showError(e?.response?.data?.Message || e?.message || "Failed to delete this project.");
        }
    }

    function handleDeleteCancel() {
        if (isDeleting) return;
        setShowDeleteDialog(false);
    }

    return (
        <Box>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "danger.outlinedBorder",
                    borderRadius: "4px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: "danger.softBg",
                        color: "danger.softColor",
                        borderBottom: "1px solid",
                        borderColor: "danger.outlinedBorder",
                    }}
                >
                    <AlertTriangle size={16} />
                    <Typography level="title-sm" sx={{ color: "inherit" }}>
                        Danger Zone
                    </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                        Delete this project
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        Permanently removes {projectName ? <strong>{projectName}</strong> : "this project"} and every
                        record created for it. This cannot be undone.
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={0.75} mt={1.5}>
                        <Typography level="body-xs" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                            Also deleted
                        </Typography>
                        {DELETE_CONSEQUENCES.map((consequence) => (
                            <Box key={consequence} display="flex" alignItems="flex-start" gap={1}>
                                <Box
                                    sx={{
                                        width: 4,
                                        height: 4,
                                        mt: "7px",
                                        flexShrink: 0,
                                        bgcolor: "danger.solidBg",
                                    }}
                                />
                                <Typography level="body-sm" color="neutral">
                                    {consequence}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                        If the job simply did not go ahead, set the project status to Cancelled instead. That keeps the
                        history intact and is reversible.
                    </Typography>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteClick}
                            disabled={isDeleting || !projectId}
                            startDecorator={<Trash2 size={16} />}
                        >
                            {isDeleting ? "Deleting..." : "Delete Project"}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Modal open={showDeleteDialog} onClose={handleDeleteCancel}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 460, borderRadius: "4px" }}>
                    <ModalClose disabled={isDeleting} />
                    <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ color: "danger.plainColor" }}>
                        <AlertTriangle size={20} />
                        <Typography level="title-md" sx={{ color: "inherit" }}>
                            Delete project
                        </Typography>
                    </Box>

                    <Typography level="body-sm" color="neutral">
                        This permanently deletes {projectName ? <strong>{projectName}</strong> : "this project"} with
                        all of its tasks, measurements, documents, cart and timeline history. This action cannot be
                        undone.
                    </Typography>

                    {projectName && (
                        <FormControl sx={{ mt: 2 }}>
                            <FormLabel sx={{ fontWeight: 400 }}>
                                Type <strong>{projectName}</strong> to confirm
                            </FormLabel>
                            <Input
                                autoFocus
                                value={confirmText}
                                placeholder={projectName}
                                disabled={isDeleting}
                                onChange={(event) => setConfirmText(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && canConfirm) {
                                        event.preventDefault();
                                        void handleDeleteConfirm();
                                    }
                                }}
                                sx={{ borderRadius: "4px" }}
                            />
                        </FormControl>
                    )}

                    <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
                        <Button variant="soft" color="neutral" onClick={handleDeleteCancel} disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting || !canConfirm}
                            loading={isDeleting}
                            startDecorator={<Trash2 size={16} />}
                        >
                            Delete Project
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
